import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { OlefinRoute } from '../../types';

/** 甲醇制烯烃（MTO）P&ID 详细图：甲醇预热 → 流化床反应器 + 再生器 → 急冷压缩 → 分离塔系 */
const media = {
  meth: { label: '甲醇', light: '#00838f', dark: '#4dd0e1', width: 2.6 },
  gas: { label: '反应油气 / 烯烃', light: '#2e7d32', dark: '#81c784', width: 2.6 },
  cat: { label: '催化剂（循环输送）', light: '#8e24aa', dark: '#ce93d8', width: 3.2, dash: '12 5' },
  water: { label: '水 / 急冷水', light: '#1565c0', dark: '#64b5f6', width: 2 },
};

const devices = [
  { id: 'e101', kind: 'exchanger' as const, x: 100, y: 181, tag: 'E-101', name: '甲醇预热' },
  { id: 'r101', kind: 'reactor' as const, x: 260, y: 128, tag: 'R-101', name: 'MTO反应器', note: '流化床 470℃' },
  { id: 'd101', kind: 'vDrum' as const, x: 430, y: 161, tag: 'D-101', name: '沉降器' },
  { id: 'rg101', kind: 'regenerator' as const, x: 203, y: 430, tag: 'RG-101', name: '再生器' },
  { id: 't101', kind: 'column' as const, x: 620, y: 101, tag: 'T-101', name: '急冷塔' },
  { id: 'c101', kind: 'compressor' as const, x: 780, y: 193, tag: 'C-101', name: '油气压缩机' },
  { id: 't102', kind: 'column' as const, x: 930, y: 101, tag: 'T-102', name: '脱甲烷塔' },
  { id: 't103', kind: 'column' as const, x: 1070, y: 101, tag: 'T-103', name: '乙烯精馏塔' },
  { id: 't104', kind: 'column' as const, x: 1210, y: 101, tag: 'T-104', name: '丙烯精馏塔' },
];

const ports: PidPort[] = [
  { id: 'p-meth', label: '甲醇', medium: 'meth', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-air', label: '主风', medium: 'water', x: 80, y: 567, direction: 'in' as const },
  { id: 'p-methane', label: '甲烷/乙烷', medium: 'gas', x: 1440, y: 52, direction: 'out' as const },
  { id: 'p-ethylene', label: '乙烯', medium: 'gas', x: 1450, y: 611, direction: 'out' as const },
  { id: 'p-propylene', label: '丙烯', medium: 'meth', x: 1450, y: 681, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 's01', from: 'p-meth', fromSide: 'right', to: 'e101', toSide: 'left', medium: 'meth' },
  { id: 's02', from: 'e101', fromSide: 'right', to: 'r101', toSide: 'left', medium: 'meth' },
  { id: 's03', from: 'r101', fromSide: 'right', to: 'd101', toSide: 'left', medium: 'gas' },
  { id: 's04', from: 'd101', fromSide: 'top', to: 't101', toSide: 'left', medium: 'gas' },
  { id: 's05', from: 't101', fromSide: 'right', to: 'c101', toSide: 'left', medium: 'gas' },
  { id: 's06', from: 'c101', fromSide: 'right', to: 't102', toSide: 'left', medium: 'gas' },
  { id: 's07', from: 't102', fromSide: 'right', to: 't103', toSide: 'left', medium: 'gas' },
  { id: 's08', from: 't103', fromSide: 'right', to: 't104', toSide: 'left', medium: 'gas' },
  { id: 's09', from: 't102', fromSide: 'top', to: 'p-methane', toSide: 'left', medium: 'gas' },
  { id: 's10', from: 't103', fromSide: 'bottom', to: 'p-ethylene', toSide: 'left', medium: 'gas' },
  { id: 's11', from: 't104', fromSide: 'bottom', to: 'p-propylene', toSide: 'left', medium: 'meth' },
  // 催化剂循环：沉降器 → 再生器 → 反应器
  { id: 's12', from: 'd101', fromSide: 'bottom', to: 'rg101', toSide: 'right', medium: 'cat' },
  { id: 's13', from: 'rg101', fromSide: 'top', to: 'r101', toSide: 'bottom', medium: 'cat' },
  { id: 's14', from: 'p-air', fromSide: 'right', to: 'rg101', toSide: 'left', medium: 'water' },
];

const MtoDiagram: React.FC<{ route: OlefinRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="MTO 装置详细流程"
    subtitle="甲醇预热 → 流化床反应器（SAPO-34 分子筛，470℃）→ 沉降器 → 急冷压缩 → 脱甲烷 → 乙烯/丙烯精馏塔系；催化剂连续再生循环（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default MtoDiagram;
